<script setup lang="ts">
/**
 * Wires CitySelect up to the server-rendered "Local favorites" landing-page
 * module: picking a city fetches that city's slides and swaps them into the
 * Phlex-rendered carousel in place.
 *
 * The HTML string is unavoidable while the card is Phlex — see the TODO on
 * Home::GiftIdeas::LocalFavorites::Slides for the Vue port that removes it.
 */
import CitySelect from '@consumer/components/CitySelect/CitySelect.vue'
import LocalFavoritesApi from '@consumer/api/LocalFavoritesApi'
import { refreshSwiper } from '@consumer/modules/swiperRefresh'
import type { SelectableCity } from '@consumer/components/CitySelect'
import type { FeaturedCity } from '@consumer/types/serializers'

withDefaults(defineProps<{
  // The city the server rendered, when the visitor has picked one.
  selectedCity?: FeaturedCity | null
  // What to show when they haven't: their own location.
  fallbackLabel: string
  // Blank-state rows — their saved locations first, then our top markets.
  cities?: FeaturedCity[]
  swiperSelector: string
  slidesWrapperSelector: string
  // The section heading's link, which lives outside the swapped markup.
  headerLinkSelector: string
  // Shown on the trigger — already includes this module's own segment.
  gaTrackingString?: string
  // Replayed to the endpoint, which rebuilds the tracking string for the
  // swapped-in cards. Raw on purpose: sending the built one would append this
  // module's segment a second time.
  sourceGaTrackingString?: string
  trackingPosition?: number
}>(), { selectedCity: null,cities: () => ([]), })

const city = ref<FeaturedCity | null>(__props.selectedCity)
const swapping = ref(false)
const error = ref<string | null>(null)

type SwapResponse = {
  city: FeaturedCity | null
  slidesHtml: string
  cta: { url: string; text: string }
}

// One swap path for every choice — only the query differs.
async function swapTo (query: Record<string, unknown>, close: () => void) {
  swapping.value = true
  error.value = null

  try {
    const { city: resolved, slidesHtml, cta } = await LocalFavoritesApi.show<SwapResponse>({
      query: {
        ...query,
        gaTrackingString: __props.sourceGaTrackingString,
        trackingPosition: __props.trackingPosition,
      },
    })

    const wrapper = document.querySelector(__props.slidesWrapperSelector)
    if (!wrapper) return

    // Trusted: this markup is rendered by our own endpoint, whose only input is
    // a slug that has to match an existing FeaturedCity. Don't widen the params.
    wrapper.innerHTML = slidesHtml
    runInsertedScripts(wrapper)
    refreshSwiper(__props.swiperSelector)

    // Null on reset, which puts the visitor's own location back on the trigger.
    city.value = resolved ?? null
    updateSectionCta(cta)
    close()
  }
  catch {
    // Leave the current city's cards in place — a failed swap shouldn't empty
    // the carousel.
    error.value = 'Something went wrong loading that city. Please try again.'
  }
  finally {
    swapping.value = false
  }
}

function swapCity (chosen: SelectableCity, close: () => void) {
  return swapTo({ citySlug: chosen.slug }, close)
}

function resetToOwnLocation (close: () => void) {
  return swapTo({ reset: true }, close)
}

// Each card carries its own inline module script — the favorite button's wiring,
// today. A <script> inserted via innerHTML is inert, so re-create each one to
// make it run; otherwise swapped-in cards render a favorite button that does
// nothing.
function runInsertedScripts (container: Element) {
  container.querySelectorAll('script').forEach((original) => {
    const script = document.createElement('script')
    for (const { name, value } of Array.from(original.attributes)) script.setAttribute(name, value)
    script.textContent = original.textContent
    original.replaceWith(script)
  })
}

// The section heading links to the same place as the carousel's CTA slide, but
// sits outside the swapped markup, so it has to be repointed by hand.
function updateSectionCta (cta: { url: string; text: string }) {
  const heading = document.querySelector<HTMLAnchorElement>(__props.headerLinkSelector)
  if (heading) heading.href = cta.url
}
</script>

<template>
  <CitySelect
    :selectedCity="city"
    :fallbackLabel="fallbackLabel"
    :cities="cities"
    :busy="swapping"
    :error="error"
    :gaTrackingString="gaTrackingString"
    testId="local-favorites-city"
    resetLabel="Near me"
    @select="swapCity"
    @reset="resetToOwnLocation"
  />
</template>
